import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Image } from '@wegroup/design-system';
import { mq } from '../../../../../../common/src/utils/styledComponentsUtils';

// Components
import { IconCarFilled } from 'wg-fe-ui';
import { retrieveAutomobileBrandsREST } from '../../../../../../common/src/legacy/services/apiRouterService';

// Types
import { Car } from '../../../../../../../types/SMT';
import { capitalize } from 'lodash';
import { AutomobileBrandProps } from '../../../../../../../types/VehicleResponse';

interface Props {
  vehicle?: Car;
}

const CarImageWithTitle: React.FC<React.PropsWithChildren<Props>> = ({
  vehicle,
  ...otherProps
}) => {
  const [brands, setBrands] = useState<AutomobileBrandProps[]>([]);

  useEffect(() => {
    if (vehicle) retrieveBrands();
  }, [vehicle]);

  const retrieveBrands = async () => {
    const [resp, status] = await retrieveAutomobileBrandsREST();
    // If status is not 200 it's not a problem
    if (status !== 200) return null;
    setBrands(resp.items);
  };

  const brand = brands.find(
    ({ brand }: AutomobileBrandProps) =>
      brand.name?.toLowerCase() === vehicle?.brand?.toLowerCase(),
  )?.brand;

  return (
    <Wrapper {...otherProps} data-test-id="ProdFac_CarImageWithTitle">
      {vehicle?.images && vehicle.images.length > 0 ? (
        <VehicleImage src={vehicle?.images?.[0] || ''} />
      ) : (
        <ImageFallback>
          {brand ? (
            <Image
              src={brand.logo_url}
              display="block"
              maxH="200px"
              maxW="200px"
              m="4rem"
            />
          ) : (
            <IconCarFilled size={300} />
          )}
        </ImageFallback>
      )}
      {brand && (
        <VehicleTypeBox data-test-id="ProdFac_CarImageWithTitle_VehicleTypeBox">
          <BrandImageWrapper data-test-id="ProdFac_CarImageWithTitle_BrandImage">
            <BrandLogo src={brand.logo_url} />
          </BrandImageWrapper>
          <VehicleLabel data-test-id="ProdFac_CarImageWithTitle_VehicleLabel">
            <VehicleName>
              {vehicle?.brand} {vehicle?.model || vehicle?.series}
            </VehicleName>
            <VehicleDescription>{vehicle?.version}</VehicleDescription>
          </VehicleLabel>
          {vehicle?.color && (
            <VehicleColor data-test-id="ProdFac_CarImageWithTitle_VehicleColor">
              <ColorIndicator color={vehicle?.color_hex || vehicle?.color} />
              {capitalize(vehicle?.color)}
            </VehicleColor>
          )}
        </VehicleTypeBox>
      )}
    </Wrapper>
  );
};

const ImageFallback = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 1rem;
  background-color: white;
  height: 100%;
  width: 100%;
  flex: 1;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
    0 0 1px rgba(0, 0, 0, 0.04);
`;

const VehicleDescription = styled.p`
  color: ${({ theme }) => theme.typo.subTitle};
`;

const VehicleTypeBox = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  background: white;
  border-radius: 1rem;
  padding: 1.6rem;

  ${mq.tablet} {
    width: unset;
    left: 1.6rem;
    right: 1.6rem;
    bottom: 1.6rem;
    box-shadow: 0 0 34.29px rgba(0, 0, 0, 0.08);
  }
`;

const BrandLogo = styled.img`
  display: block;
  max-height: 100%;
  max-width: 100%;
  margin: auto;
`;

const BrandImageWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0.8rem;
  height: 4.8rem;
  width: 4.8rem;
`;

const ColorIndicator = styled.div<{ color?: string }>`
  border-radius: 50%;
  background-color: ${({ color }) => color};
  height: 1.2rem;
  width: 1.2rem;
  border: 1px solid ${({ theme }) => theme.gray[300]};
  margin-right: 1rem;
`;

const VehicleImage = styled.img`
  object-fit: cover;
  border-top-right-radius: 10px;
  border-top-left-radius: 10px;
  max-width: 100%;
  height: 100%;

  ${mq.tablet} {
    max-height: 28rem;
    max-width: 100%;
  }
`;

const VehicleLabel = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 1.6rem;
  height: 100%;
`;

const VehicleName = styled.div`
  color: #050505;
  font-weight: 600;
  font-size: 1.6rem;
`;

const VehicleColor = styled.span`
  color: #8990a3;
  display: flex;
  flex-direction: row;
  font-size: 1.4rem;
  margin-top: 1rem;
  align-items: center;
`;

const Wrapper = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  max-width: 100%;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.04), 0 2px 6px rgba(0, 0, 0, 0.04),
    0 0 1px rgba(0, 0, 0, 0.04);
  border: 1px solid ${({ theme }) => theme.gray[50]};

  margin-bottom: 2.4rem;
  flex-direction: column;

  ${mq.laptopL} {
    box-shadow: none;
    max-width: 48rem;
    height: 100%;

    margin-bottom: 0;
    margin-right: 4.8rem;
  }
`;

export default CarImageWithTitle;
